import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  DollarSign,
  TrendingUp,
  Clock,
  Download,
  Eye,
  Calendar,
  Target,
  Award,
  CreditCard,
  AlertCircle
} from 'lucide-react';

interface EarningsSummary {
  total: { [currency: string]: { amount: number; count: number } };
  available: { [currency: string]: { amount: number; count: number } };
  pending: { [currency: string]: { amount: number; count: number } };
  withdrawn: { [currency: string]: { amount: number; count: number } };
}

interface Earning {
  _id: string;
  amount: number;
  currency: string;
  status: string;
  category: string;
  description: string;
  paymentDate: string;
  project: {
    _id: string;
    title: string;
  };
  metadata: {
    projectTitle: string;
    milestoneTitle: string;
    clientName: string;
  };
  createdAt: string;
}

// Get the correct API base URL based on environment
const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};


const EarningsOverview: React.FC = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState<EarningsSummary>({
    total: {},
    available: {},
    pending: {},
    withdrawn: {}
  });
  const [recentEarnings, setRecentEarnings] = useState<Earning[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const API_BASE_URL = getApiBaseUrl();

  useEffect(() => {
    fetchEarningsData();
  }, []);

  const fetchEarningsData = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      
      // Fetch earnings summary
      const summaryResponse = await fetch(`${API_BASE_URL}/earnings/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        setSummary(summaryData);
      }

      // Fetch recent earnings
      const earningsResponse = await fetch(`${API_BASE_URL}/earnings?limit=10`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (earningsResponse.ok) {
        const earningsData = await earningsResponse.json();
        setRecentEarnings(earningsData.earnings || []);
      }

    } catch (error) {
      setError('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'withdrawn': return 'text-blue-600 bg-blue-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'milestone-payment': return <Target className="w-4 h-4" />;
      case 'performance-bonus': return <Award className="w-4 h-4" />;
      case 'admin-fee': return <CreditCard className="w-4 h-4" />;
      default: return <DollarSign className="w-4 h-4" />;
    }
  };

  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  // Mock data for demonstration
  const mockSummary = {
    total: { USD: { amount: 15750, count: 12 } },
    available: { USD: { amount: 3250, count: 3 } },
    pending: { USD: { amount: 2100, count: 2 } },
    withdrawn: { USD: { amount: 10400, count: 7 } }
  };

  const mockEarnings = [
    {
      _id: '1',
      amount: 1500,
      currency: 'USD',
      status: 'available',
      category: 'milestone-payment',
      description: 'Milestone 2 - E-commerce Platform',
      paymentDate: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
      project: { _id: '1', title: 'E-commerce Platform' },
      metadata: {
        projectTitle: 'E-commerce Platform',
        milestoneTitle: 'Frontend Development',
        clientName: 'John Smith'
      },
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString()
    },
    {
      _id: '2',
      amount: 800,
      currency: 'USD',
      status: 'pending',
      category: 'milestone-payment',
      description: 'Milestone 1 - Mobile App',
      paymentDate: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
      project: { _id: '2', title: 'Mobile App Development' },
      metadata: {
        projectTitle: 'Mobile App Development',
        milestoneTitle: 'UI Design',
        clientName: 'Sarah Johnson'
      },
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString()
    }
  ];

  const displaySummary = Object.keys(summary.total).length > 0 ? summary : mockSummary;
  const displayEarnings = recentEarnings.length > 0 ? recentEarnings : mockEarnings;

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-16 bg-gray-200 rounded-lg animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-sm border animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-20 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-16 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-24"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Earnings Overview</h1>
          <p className="text-gray-600 mt-1">Track your earnings and payments</p>
        </div>
        <div className="flex items-center space-x-3">
          <Link
            to="/dashboard/earnings/withdrawals"
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
          >
            <Download className="w-4 h-4 mr-2" />
            Request Withdrawal
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Earnings</p>
              <p className="text-2xl font-bold text-gray-900">
                {Object.entries(displaySummary.total).map(([currency, data]) => 
                  formatCurrency(data.amount, currency)
                ).join(', ')}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600">+15% this month</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Available Balance</p>
              <p className="text-2xl font-bold text-gray-900">
                {Object.entries(displaySummary.available).map(([currency, data]) => 
                  formatCurrency(data.amount, currency)
                ).join(', ')}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Download className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-gray-600">Ready for withdrawal</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Earnings</p>
              <p className="text-2xl font-bold text-gray-900">
                {Object.entries(displaySummary.pending).map(([currency, data]) => 
                  formatCurrency(data.amount, currency)
                ).join(', ')}
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-gray-600">Awaiting release</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Withdrawn</p>
              <p className="text-2xl font-bold text-gray-900">
                {Object.entries(displaySummary.withdrawn).map(([currency, data]) => 
                  formatCurrency(data.amount, currency)
                ).join(', ')}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Award className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-gray-600">
              {Object.values(displaySummary.withdrawn).reduce((sum, data) => sum + data.count, 0)} withdrawals
            </span>
          </div>
        </div>
      </div>

      {/* Recent Earnings */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Recent Earnings</h2>
            <Link
              to="/dashboard/earnings/history"
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              View all
            </Link>
          </div>
        </div>

        <div className="p-6">
          {displayEarnings.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No earnings yet</h3>
              <p className="text-gray-600">Earnings will appear here when you complete milestones.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {displayEarnings.map((earning) => (
                <div
                  key={earning._id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      {getCategoryIcon(earning.category)}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{earning.description}</h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                        <span>Project: {earning.metadata.projectTitle}</span>
                        <span>Client: {earning.metadata.clientName}</span>
                        <span>{getRelativeTime(earning.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">
                        {formatCurrency(earning.amount, earning.currency)}
                      </div>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(earning.status)}`}
                      >
                        {earning.status}
                      </span>
                    </div>
                    <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-lg text-white">
          <Download className="w-8 h-8 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Request Withdrawal</h3>
          <p className="text-green-100 mb-4">
            Withdraw your available earnings to your bank account
          </p>
          <Link
            to="/dashboard/earnings/withdrawals"
            className="inline-flex items-center px-4 py-2 bg-white text-green-600 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          >
            Get Started
          </Link>
        </div>

        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-lg text-white">
          <Calendar className="w-8 h-8 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Earnings History</h3>
          <p className="text-blue-100 mb-4">
            View detailed history of all your earnings and payments
          </p>
          <Link
            to="/dashboard/earnings/history"
            className="inline-flex items-center px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          >
            View History
          </Link>
        </div>
      </div>
    </div>
  );
};

export default EarningsOverview;